import type {
  BodyUUID,
  Equality,
  EqualityUUID,
  Joint,
  JointUUID,
  MaterialUUID,
  MeshUUID,
  TextureUUID,
  Vector3,
  Vector4,
  WorldBody,
} from "../types/scene";
import { createDefaultPhongMaterial } from "../utils/materialDefaults";
import { generateUuid } from "../utils/id";
import { logger } from "../utils/logger";
import type {
  MJCFParseResult,
  ParsedBodiesMap,
  ParsedGeom,
  ParsedInertia,
  ParsedMaterialsMap,
  ParsedMeshesMap,
  ParsedTexturesMap,
} from "./mjcf-parser/types";

type UrdfJointType = "fixed" | "revolute" | "continuous" | "prismatic" | "floating" | "planar";

type LinkInfo = {
  name: string;
  element: Element;
  bodyUuid: BodyUUID;
  skippedSyntheticWorld: boolean;
};

type JointInfo = {
  name: string;
  type: UrdfJointType | string;
  element: Element;
  parent: string;
  child: string;
};

type UrdfVariableMap = Map<string, string>;

const MESH_EXTENSIONS = /\.(obj|stl|dae)(?:[?#].*)?$/i;
const TEXTURE_EXTENSIONS = /\.(png|jpg|jpeg|webp|bmp|tga|ktx|ktx2)(?:[?#].*)?$/i;

const normalizeNumbers = (value: string | null | undefined): number[] | undefined => {
  if (!value) return undefined;
  const values = value
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((entry) => Number.isFinite(entry));
  return values.length > 0 ? values : undefined;
};

const vector3 = (value: string | null | undefined, fallback: Vector3): Vector3 => {
  const values = normalizeNumbers(value);
  return values && values.length >= 3 ? [values[0], values[1], values[2]] : fallback;
};

const finiteNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, finiteNumber(value, 0)));

const normalizePackageUri = (value: string | null | undefined): string => {
  const raw = String(value || "")
    .trim()
    .replace(/\\/g, "/");
  if (!raw) return "";
  if (raw.startsWith("package://")) return raw.slice("package://".length).replace(/^\/+/, "");
  if (raw.startsWith("file://")) return raw.slice("file://".length).replace(/^\/+/, "");
  return raw.replace(/^\.\/+/, "").replace(/^\/+/, "");
};

const basename = (value: string): string => {
  const normalized = normalizePackageUri(value);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

const getDirectChildren = (element: Element, tagName: string): Element[] =>
  Array.from(element.children).filter((child) => child.tagName.toLowerCase() === tagName);

const getFirstDirectChild = (element: Element, tagName: string): Element | null =>
  getDirectChildren(element, tagName)[0] || null;

const getLocalTagName = (element: Element): string => {
  const localName = element.localName || element.tagName;
  return localName.toLowerCase();
};

const getDirectChildrenByLocalName = (element: Element, tagName: string): Element[] =>
  Array.from(element.children).filter((child) => getLocalTagName(child) === tagName);

const parseXacroArgReference = (value: string): string | null => {
  const match = value.trim().match(/^\$\(arg\s+([^)]+)\)$/);
  return match?.[1]?.trim() || null;
};

const collectUrdfVariables = (robot: Element): UrdfVariableMap => {
  const variables: UrdfVariableMap = new Map();
  getDirectChildrenByLocalName(robot, "arg").forEach((argElement) => {
    const name = argElement.getAttribute("name")?.trim();
    if (!name) return;
    variables.set(name, argElement.getAttribute("default") || "");
  });
  getDirectChildrenByLocalName(robot, "property").forEach((propertyElement) => {
    const name = propertyElement.getAttribute("name")?.trim();
    if (!name) return;
    const value = propertyElement.getAttribute("value") || "";
    const argRef = parseXacroArgReference(value);
    variables.set(name, argRef ? variables.get(argRef) || "" : value);
  });
  return variables;
};

const substituteUrdfVariables = (
  value: string | null | undefined,
  variables: UrdfVariableMap
): string => {
  if (!value) return "";
  return value.replace(/\$\{([^}]+)\}/g, (match, rawName) => {
    const name = String(rawName || "").trim();
    return variables.has(name) ? variables.get(name) || "" : match;
  });
};

const rpyToQuat = (rpy: number[] | undefined): Vector4 => {
  const roll = rpy?.[0] || 0;
  const pitch = rpy?.[1] || 0;
  const yaw = rpy?.[2] || 0;
  const cy = Math.cos(yaw * 0.5);
  const sy = Math.sin(yaw * 0.5);
  const cp = Math.cos(pitch * 0.5);
  const sp = Math.sin(pitch * 0.5);
  const cr = Math.cos(roll * 0.5);
  const sr = Math.sin(roll * 0.5);
  const quat: Vector4 = [
    cr * cp * cy + sr * sp * sy,
    sr * cp * cy - cr * sp * sy,
    cr * sp * cy + sr * cp * sy,
    cr * cp * sy - sr * sp * cy,
  ];
  const mag = Math.hypot(quat[0], quat[1], quat[2], quat[3]);
  return mag > 0 ? [quat[0] / mag, quat[1] / mag, quat[2] / mag, quat[3] / mag] : [1, 0, 0, 0];
};

const parseOrigin = (element: Element | null): { position: Vector3; orientation: Vector4 } => {
  const origin = element ? getFirstDirectChild(element, "origin") : null;
  return {
    position: vector3(origin?.getAttribute("xyz"), [0, 0, 0]),
    orientation: rpyToQuat(normalizeNumbers(origin?.getAttribute("rpy"))),
  };
};

const createJoint = (
  name: string,
  kind: "hinge" | "slide" | "free",
  axis: Vector3,
  limitElement: Element | null,
  dynamicsElement: Element | null,
  limited: boolean
): Joint => {
  const effort = finiteNumber(limitElement?.getAttribute("effort"), NaN);
  const hasEffort = Number.isFinite(effort);
  const base = {
    uuid: generateUuid() as JointUUID,
    name,
    kind,
    group: 0,
    stiffness: 0,
    damping: finiteNumber(dynamicsElement?.getAttribute("damping"), 0),
    frictionloss: finiteNumber(dynamicsElement?.getAttribute("friction"), 0),
    springDamper: [0, 0] as [number, number],
    armature: 0,
    frictionSolverReference: [0.02, 1] as [number, number],
    frictionSolverImpedance: [0.9, 0.95, 0.001, 0.5, 2] as [number, number, number, number, number],
  };
  if (kind === "free") {
    return base as Joint;
  }

  const lower = finiteNumber(limitElement?.getAttribute("lower"), -Math.PI);
  const upper = finiteNumber(limitElement?.getAttribute("upper"), Math.PI);
  return {
    ...base,
    kind,
    position: [0, 0, 0],
    axis,
    ref: 0,
    initialRef: 0,
    restingPosition: 0,
    range: [lower, upper],
    useLimits: limited ? "true" : "false",
    contactMargin: 0.01,
    limitSolverReference: [0.02, 1],
    limitSolverImpedance: [0.9, 0.95, 0.001, 0.5, 2],
    actfrclimited: hasEffort ? 1 : 0,
    actfrcrange: hasEffort ? [-Math.abs(effort), Math.abs(effort)] : [0, 0],
    actgravcomp: 0,
  } as Joint;
};

export class URDFParser {
  xmlDoc!: Document;
  parseWarnings: string[] = [];

  warn(...args: unknown[]): void {
    const message = args
      .map((arg) => (typeof arg === "string" ? arg : JSON.stringify(arg)))
      .join(" ")
      .trim();
    if (message) this.parseWarnings.push(message);
    logger.warn(...args);
  }

  async parseFile(xmlFile: File): Promise<MJCFParseResult> {
    this.parseWarnings = [];
    const text = await xmlFile.text();
    const parser = new DOMParser();
    this.xmlDoc = parser.parseFromString(text, "application/xml");
    const parseError = this.xmlDoc.querySelector("parsererror");
    if (parseError) throw new Error("URDF XML parsing failed");
    const root = this.xmlDoc.documentElement;
    if (!root || root.tagName.toLowerCase() !== "robot") {
      throw new Error("URDF import expected a <robot> root element.");
    }
    return this.parseRobot(root);
  }

  private parseRobot(robot: Element): MJCFParseResult {
    this.warnUnsupportedElements(robot);
    const variables = collectUrdfVariables(robot);
    const links = this.parseLinks(robot, variables);
    const joints = this.parseJointInfos(robot, variables);
    const syntheticWorldLinks = this.findSyntheticWorldLinks(links, joints);
    syntheticWorldLinks.forEach((name) => {
      const link = links.get(name);
      if (link) link.skippedSyntheticWorld = true;
    });

    const bodyChildren = new Map<string, string[]>();
    const parentByChild = new Map<string, JointInfo>();
    joints.forEach((joint) => {
      if (!joint.parent || !joint.child) return;
      const parentLink = links.get(joint.parent);
      const childLink = links.get(joint.child);
      if (!childLink || childLink.skippedSyntheticWorld) {
        this.warn(
          `[URDFParser] Joint "${joint.name}" references unsupported or missing child link "${joint.child}".`
        );
        return;
      }
      if (parentLink && !parentLink.skippedSyntheticWorld) {
        const children = bodyChildren.get(joint.parent) || [];
        children.push(joint.child);
        bodyChildren.set(joint.parent, children);
      }
      parentByChild.set(joint.child, joint);
    });

    const materials = this.parseMaterials(robot);
    const textures = this.parseTextures(robot);
    const meshes: ParsedMeshesMap = {};
    const sceneJoints: Joint[] = [];
    const equalities: Record<EqualityUUID, Equality> = {};
    const bodies: ParsedBodiesMap = {};
    const worldBodies: WorldBody[] = [];
    const jointNameToUuid = new Map<string, JointUUID>();
    const meshKeyToUuid = new Map<string, MeshUUID>();

    const getMeshUuid = (filename: string, scale: Vector3): MeshUUID => {
      const key = `${filename}|${scale.join(" ")}`;
      const existing = meshKeyToUuid.get(key);
      if (existing) return existing;
      const uuid = generateUuid() as MeshUUID;
      meshKeyToUuid.set(key, uuid);
      meshes[uuid] = { uuid, name: basename(filename) || uuid, url: filename, scale };
      return uuid;
    };

    links.forEach((link, linkName) => {
      if (link.skippedSyntheticWorld) return;
      const parentJoint = parentByChild.get(linkName);
      const origin = parseOrigin(parentJoint?.element || null);
      const bodyJointUuids: JointUUID[] = [];
      if (parentJoint) {
        const parsedJoint = this.createSceneJoint(parentJoint);
        if (parsedJoint) {
          sceneJoints.push(parsedJoint);
          jointNameToUuid.set(parentJoint.name, parsedJoint.uuid);
          bodyJointUuids.push(parsedJoint.uuid);
        }
      }
      bodies[link.bodyUuid] = {
        uuid: link.bodyUuid,
        name: link.name,
        position: origin.position,
        orientation: origin.orientation,
        joints: bodyJointUuids,
        sites: [],
        children: [],
        geoms: this.parseLinkGeoms(link.element, materials, getMeshUuid),
        inertia: this.parseInertial(link.element),
      };
    });

    bodyChildren.forEach((childNames, parentName) => {
      const parent = links.get(parentName);
      if (!parent || parent.skippedSyntheticWorld) return;
      const body = bodies[parent.bodyUuid];
      if (!body) return;
      body.children = childNames
        .map((childName) => links.get(childName))
        .filter((child): child is LinkInfo => !!child && !child.skippedSyntheticWorld)
        .map((child) => child.bodyUuid);
    });

    links.forEach((link, linkName) => {
      if (link.skippedSyntheticWorld) return;
      const parentJoint = parentByChild.get(linkName);
      const parent = parentJoint ? links.get(parentJoint.parent) : null;
      if (!parent || parent.skippedSyntheticWorld) {
        worldBodies.push({ uuid: link.bodyUuid, name: link.name });
      }
    });

    this.parseMimicEqualities(joints, jointNameToUuid, equalities);

    return {
      xmlDoc: this.xmlDoc,
      parseWarnings: this.parseWarnings,
      ambiguousGeomRoleCandidates: [],
      joints: sceneJoints,
      bodies,
      worldBodies,
      sites: {},
      cameras: {},
      materials,
      textures,
      actuators: {},
      tendons: {},
      meshes,
      hfields: {},
      collisionClassDefaults: {},
      collisionTypeMap: {},
      referencedFiles: this.parseReferencedFiles(robot),
      keyframes: {},
      keyframeDetails: {},
      preservedMjcfSections: {},
      equalities,
      contactPairs: [],
      contactExcludes: [],
      sensors: {},
    };
  }

  private parseLinks(robot: Element, variables: UrdfVariableMap): Map<string, LinkInfo> {
    const links = new Map<string, LinkInfo>();
    getDirectChildren(robot, "link").forEach((linkElement) => {
      const name = substituteUrdfVariables(linkElement.getAttribute("name"), variables).trim();
      if (!name) {
        this.warn("[URDFParser] Skipping link without a name.");
        return;
      }
      links.set(name, {
        name,
        element: linkElement,
        bodyUuid: generateUuid() as BodyUUID,
        skippedSyntheticWorld: false,
      });
    });
    return links;
  }

  private parseJointInfos(robot: Element, variables: UrdfVariableMap): JointInfo[] {
    return getDirectChildren(robot, "joint")
      .map((jointElement, index) => {
        const name =
          substituteUrdfVariables(jointElement.getAttribute("name"), variables).trim() ||
          `joint_${index + 1}`;
        const type = jointElement.getAttribute("type")?.trim().toLowerCase() || "fixed";
        return {
          name,
          type,
          element: jointElement,
          parent:
            substituteUrdfVariables(
              getFirstDirectChild(jointElement, "parent")?.getAttribute("link"),
              variables
            ).trim() || "",
          child:
            substituteUrdfVariables(
              getFirstDirectChild(jointElement, "child")?.getAttribute("link"),
              variables
            ).trim() || "",
        };
      })
      .filter((joint) => {
        if (joint.parent && joint.child) return true;
        this.warn(
          `[URDFParser] Skipping joint "${joint.name}" because parent or child is missing.`
        );
        return false;
      });
  }

  private findSyntheticWorldLinks(links: Map<string, LinkInfo>, joints: JointInfo[]): Set<string> {
    const result = new Set<string>();
    const world = links.get("world");
    if (!world || !this.isEmptyLink(world.element)) return result;
    const childJoints = joints.filter((joint) => joint.child === "world");
    const parentJoints = joints.filter((joint) => joint.parent === "world");
    if (childJoints.length === 0 && parentJoints.length > 0) result.add("world");
    return result;
  }

  private isEmptyLink(link: Element): boolean {
    return ["visual", "collision", "inertial"].every(
      (tagName) => getDirectChildren(link, tagName).length === 0
    );
  }

  private createSceneJoint(joint: JointInfo): Joint | null {
    const axis = vector3(
      getFirstDirectChild(joint.element, "axis")?.getAttribute("xyz"),
      [0, 0, 1]
    );
    const limit = getFirstDirectChild(joint.element, "limit");
    const dynamics = getFirstDirectChild(joint.element, "dynamics");
    if (joint.type === "fixed") return null;
    if (joint.type === "planar") {
      this.warn(
        `[URDFParser] Planar joint "${joint.name}" imported as fixed because planar joints are not supported.`
      );
      return null;
    }
    if (joint.type === "revolute")
      return createJoint(joint.name, "hinge", axis, limit, dynamics, true);
    if (joint.type === "continuous")
      return createJoint(joint.name, "hinge", axis, limit, dynamics, false);
    if (joint.type === "prismatic")
      return createJoint(joint.name, "slide", axis, limit, dynamics, true);
    if (joint.type === "floating")
      return createJoint(joint.name, "free", axis, limit, dynamics, false);
    this.warn(
      `[URDFParser] Joint "${joint.name}" has unsupported type "${joint.type}" and was imported as fixed.`
    );
    return null;
  }

  private parseMaterials(robot: Element): ParsedMaterialsMap {
    const materials: ParsedMaterialsMap = {};
    const addMaterial = (name: string, materialElement: Element): string => {
      const existing = Object.entries(materials).find(([, material]) => material?.name === name);
      if (existing) return existing[0];
      const colorElement = getFirstDirectChild(materialElement, "color");
      const color = vector3(colorElement?.getAttribute("rgba"), [0.75, 0.75, 0.75]);
      const alpha = finiteNumber(normalizeNumbers(colorElement?.getAttribute("rgba"))?.[3], 1);
      const textureFilename = normalizePackageUri(
        getFirstDirectChild(materialElement, "texture")?.getAttribute("filename")
      );
      const uuid = generateUuid() as MaterialUUID;
      materials[uuid] = createDefaultPhongMaterial(name, {
        uuid,
        color: [clamp01(color[0]), clamp01(color[1]), clamp01(color[2]), clamp01(alpha)],
        ...(textureFilename ? { colorMap: textureFilename as unknown as TextureUUID } : {}),
      });
      return uuid;
    };

    robot.querySelectorAll("material").forEach((materialElement) => {
      const name = materialElement.getAttribute("name")?.trim();
      if (!name) return;
      addMaterial(name, materialElement);
    });
    return materials;
  }

  private parseTextures(robot: Element): ParsedTexturesMap {
    const textures: ParsedTexturesMap = {};
    robot.querySelectorAll("material > texture").forEach((textureElement) => {
      const filename = normalizePackageUri(textureElement.getAttribute("filename"));
      if (!filename) return;
      textures[filename] = { file: filename, kind: "2d" };
    });
    return textures;
  }

  private parseLinkGeoms(
    linkElement: Element,
    materials: ParsedMaterialsMap,
    getMeshUuid: (filename: string, scale: Vector3) => MeshUUID
  ): ParsedGeom[] {
    const geoms: ParsedGeom[] = [];
    let visualIndex = 1;
    let collisionIndex = 1;
    getDirectChildren(linkElement, "visual").forEach((visual) => {
      const geom = this.parseGeometryElement(
        visual,
        true,
        `visual_${visualIndex++}`,
        materials,
        getMeshUuid
      );
      if (geom) geoms.push(geom);
    });
    getDirectChildren(linkElement, "collision").forEach((collision) => {
      const geom = this.parseGeometryElement(
        collision,
        false,
        `geom_${collisionIndex++}`,
        materials,
        getMeshUuid
      );
      if (geom) geoms.push(geom);
    });
    return geoms;
  }

  private parseGeometryElement(
    element: Element,
    visualOnly: boolean,
    fallbackName: string,
    materials: ParsedMaterialsMap,
    getMeshUuid: (filename: string, scale: Vector3) => MeshUUID
  ): ParsedGeom | null {
    const geometry = getFirstDirectChild(element, "geometry");
    if (!geometry) return null;
    const { position, orientation } = parseOrigin(element);
    const materialName = getFirstDirectChild(element, "material")?.getAttribute("name")?.trim();
    const materialUuid = materialName
      ? Object.entries(materials).find(([, material]) => material?.name === materialName)?.[0]
      : undefined;
    const base = {
      uuid: generateUuid(),
      name: element.getAttribute("name")?.trim() || fallbackName,
      position,
      orientation,
      visualOnly,
      materials: materialUuid ? [materialUuid] : [],
      contactCategory: visualOnly ? 0 : undefined,
      contactAffinity: visualOnly ? 0 : undefined,
    };

    const box = getFirstDirectChild(geometry, "box");
    if (box) {
      const size = vector3(box.getAttribute("size"), [0.1, 0.1, 0.1]).map((v) => v / 2);
      return { ...base, kind: "box", size };
    }
    const sphere = getFirstDirectChild(geometry, "sphere");
    if (sphere) {
      return { ...base, kind: "sphere", size: [finiteNumber(sphere.getAttribute("radius"), 0.1)] };
    }
    const cylinder = getFirstDirectChild(geometry, "cylinder");
    if (cylinder) {
      const radius = finiteNumber(cylinder.getAttribute("radius"), 0.1);
      const halfLength = finiteNumber(cylinder.getAttribute("length"), 0.2) / 2;
      return { ...base, kind: "cylinder", size: [radius, halfLength] };
    }
    const mesh = getFirstDirectChild(geometry, "mesh");
    if (mesh) {
      const filename = normalizePackageUri(mesh.getAttribute("filename"));
      if (!filename) {
        this.warn(
          `[URDFParser] Mesh geometry "${base.name}" is missing a filename and was skipped.`
        );
        return null;
      }
      const scale = vector3(mesh.getAttribute("scale"), [1, 1, 1]);
      return { ...base, kind: "mesh", size: [1, 1, 1], mesh: getMeshUuid(filename, scale) };
    }
    this.warn(
      `[URDFParser] Geometry "${base.name}" has an unsupported or unknown geometry type and was skipped.`
    );
    return null;
  }

  private parseInertial(linkElement: Element): ParsedInertia | undefined {
    const inertial = getFirstDirectChild(linkElement, "inertial");
    if (!inertial) return undefined;
    const mass = finiteNumber(getFirstDirectChild(inertial, "mass")?.getAttribute("value"), 1);
    const inertia = getFirstDirectChild(inertial, "inertia");
    if (!inertia) {
      this.warn(
        `[URDFParser] Link "${linkElement.getAttribute("name")}" inertial is missing inertia tensor values.`
      );
    }
    const { position, orientation } = parseOrigin(inertial);
    const ixx = finiteNumber(inertia?.getAttribute("ixx"), 1e-6);
    const iyy = finiteNumber(inertia?.getAttribute("iyy"), 1e-6);
    const izz = finiteNumber(inertia?.getAttribute("izz"), 1e-6);
    const ixy = finiteNumber(inertia?.getAttribute("ixy"), 0);
    const ixz = finiteNumber(inertia?.getAttribute("ixz"), 0);
    const iyz = finiteNumber(inertia?.getAttribute("iyz"), 0);
    return {
      uuid: generateUuid(),
      name: `inertial_${linkElement.getAttribute("name") || "link"}`,
      mass: Math.max(mass, 1e-9),
      position,
      orientation,
      diagonal: [ixx, iyy, izz],
      fullinertia: [ixx, iyy, izz, ixy, ixz, iyz],
    };
  }

  private parseMimicEqualities(
    joints: JointInfo[],
    jointNameToUuid: Map<string, JointUUID>,
    equalities: Record<EqualityUUID, Equality>
  ): void {
    joints.forEach((targetJoint) => {
      const mimic = getFirstDirectChild(targetJoint.element, "mimic");
      if (!mimic) return;
      const sourceName = mimic.getAttribute("joint")?.trim();
      const sourceUuid = sourceName ? jointNameToUuid.get(sourceName) : undefined;
      const targetUuid = jointNameToUuid.get(targetJoint.name);
      if (!sourceUuid || !targetUuid) {
        this.warn(
          `[URDFParser] Mimic on joint "${targetJoint.name}" could not be represented because the source or target joint was not imported.`
        );
        return;
      }
      const uuid = generateUuid() as EqualityUUID;
      equalities[uuid] = {
        uuid,
        name: `${targetJoint.name}_mimic`,
        type: "joint",
        objtype: "joint",
        obj1: targetUuid,
        obj2: sourceUuid,
        data: [
          finiteNumber(mimic.getAttribute("offset"), 0),
          finiteNumber(mimic.getAttribute("multiplier"), 1),
          0,
          0,
          0,
        ],
        active: true,
      };
    });
  }

  private warnUnsupportedElements(robot: Element): void {
    const unsupported = ["transmission", "gazebo", "safety_controller", "calibration"];
    unsupported.forEach((tagName) => {
      const count = robot.querySelectorAll(tagName).length;
      if (count > 0)
        this.warn(
          `[URDFParser] ${count} <${tagName}> element(s) are not supported by URDF import v1 and were ignored.`
        );
    });
  }

  private parseReferencedFiles(robot: Element): string[] {
    const files = new Set<string>();
    robot.querySelectorAll("mesh").forEach((mesh) => {
      const filename = normalizePackageUri(mesh.getAttribute("filename"));
      if (filename && MESH_EXTENSIONS.test(filename)) files.add(filename);
    });
    robot.querySelectorAll("texture").forEach((texture) => {
      const filename = normalizePackageUri(texture.getAttribute("filename"));
      if (filename && TEXTURE_EXTENSIONS.test(filename)) files.add(filename);
    });
    return Array.from(files);
  }
}
